import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { adminGuard } from './core/guards/admin.guard';
import { gestoriaAdminGuard } from './core/guards/gestoria-admin.guard';
import { superAdminGuard } from './core/guards/super-admin.guard';
import { FEATURE_FLAGS } from './core/utils/feature-flags';

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('./pages/auth/login/login.component').then(m => m.LoginComponent),
  },
  {
    path: 'register',
    loadComponent: () => import('./pages/auth/register/register.component').then(m => m.RegisterComponent),
  },
  {
    path: 'auth/microsoft/callback',
    loadComponent: () => import('./pages/auth/microsoft-callback/microsoft-callback').then(m => m.MicrosoftCallback),
  },
  {
    path: 'forgot-password',
    loadComponent: () => import('./pages/auth/forgot-password/forgot-password').then(m => m.ForgotPassword),
  },
  {
    path: 'reset-password',
    loadComponent: () => import('./pages/auth/reset-password/reset-password').then(m => m.ResetPassword),
  },
  {
    path: 'invitaciones/aceptar',
    loadComponent: () => import('./pages/auth/accept-invitation/accept-invitation').then(m => m.AcceptInvitation),
  },
  {
    path: 'gestoria/ajustes/verificar-remitente',
    loadComponent: () => import('./pages/auth/verify-sender-email/verify-sender-email').then(m => m.VerifySenderEmail),
  },
  {
    path: 'desconexion/aceptar/:token',
    loadComponent: () => import('./pages/public/disconnection-acceptance/disconnection-acceptance').then(m => m.DisconnectionAcceptance),
  },
  {
    path: 'home',
    loadComponent: () => import('./pages/dashboard/home/home.component').then(m => m.HomeComponent),
    canActivate: [authGuard],
    data: { title: 'Inicio', subtitle: 'Panel de control' },
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        loadComponent: () => import('./pages/dashboard/dashboard-admin/dashboard-admin').then(m => m.DashboardAdmin),
        data: { title: 'Panel', subtitle: '' },
      },
      {
        path: 'inicio',
        loadComponent: () => import('./pages/dashboard/dashboard-cliente/dashboard-cliente').then(m => m.DashboardCliente),
        data: { title: 'Panel', subtitle: '' },
      },
      {
        path: 'gestorias/nueva',
        canActivate: [superAdminGuard],
        loadComponent: () => import('./pages/dashboard/firm-management/new-firm/new-firm').then(m => m.NewFirm),
        data: {
          title: 'Perfil de gestoría',
          subtitle: 'Datos legales y estructurales de la entidad',
          breadcrumbs: [
            { label: 'Gestorías', url: '/home/gestorias' },
            { label: 'Perfil de gestoría' },
          ],
        },
      },
      {
        path: 'gestorias/:id/editar',
        canActivate: [superAdminGuard],
        loadComponent: () => import('./pages/dashboard/firm-management/new-firm/new-firm').then(m => m.NewFirm),
        data: {
          title: 'Editar gestoría',
          subtitle: 'Actualiza los datos legales y estructurales de la entidad',
          breadcrumbs: [
            { label: 'Gestorías', url: '/home/gestorias' },
            { label: 'Editar gestoría' },
          ],
        },
      },
      {
        path: 'gestorias/:id',
        canActivate: [superAdminGuard],
        loadComponent: () => import('./pages/dashboard/firm-management/new-firm/new-firm').then(m => m.NewFirm),
        data: {
          title: 'Perfil de gestoría',
          subtitle: 'Datos legales y estructurales de la entidad',
          breadcrumbs: [
            { label: 'Gestorías', url: '/home/gestorias' },
            { label: 'Perfil de gestoría' },
          ],
        },
      },
      {
        path: 'gestorias',
        canActivate: [superAdminGuard],
        loadComponent: () => import('./pages/dashboard/firm-management/firm-management').then(m => m.FirmManagement),
        data: { title: 'Gestorías', subtitle: 'Gestiona las asesorías y despachos que operan en la plataforma' },
      },
      {
        path: 'profile',
        loadComponent: () => import('./pages/dashboard/profile/profile.component').then(m => m.ProfileComponent),
        data: { title: 'Perfil', subtitle: 'Gestión de tu cuenta', profileMode: 'profile' },
      },
      {
        path: 'me/empresa',
        loadComponent: () => import('./pages/dashboard/profile/profile.component').then(m => m.ProfileComponent),
        data: { title: 'Mi empresa', subtitle: 'Datos de tu empresa y accesos', profileMode: 'company' },
      },
      // 2026-08-04: modulo de solicitudes de activacion retirado del area cliente; acceso directo redirigido sin error.
      // Ruta de cliente anterior reservada para reactivacion futura: path 'me/solicitudes'.
      {
        path: 'me/solicitudes',
        redirectTo: '/home/inicio',
        pathMatch: 'full',
      },
      {
        path: 'configuracion',
        loadComponent: () => import('./pages/dashboard/profile/profile.component').then(m => m.ProfileComponent),
        data: { title: 'Configuración', subtitle: 'Gestión de tu cuenta', profileMode: 'configuration' },
      },
      {
        path: 'ajustes',
        canActivate: [gestoriaAdminGuard],
        loadComponent: () => import('./pages/dashboard/gestoria-settings/gestoria-settings').then(m => m.GestoriaSettingsComponent),
        data: { title: 'Configuración', subtitle: 'Ajustes de gestoría' },
      },
      {
        path: 'mi-grupo',
        loadComponent: () => import('./pages/dashboard/profile/mi-grupo.component').then(m => m.MiGrupoComponent),
        data: { title: 'Vista del grupo', subtitle: 'Resumen de tu grupo empresarial' },
      },
      {
        path: 'mi-grupo/empresas',
        loadComponent: () => import('./pages/dashboard/profile/mi-grupo.component').then(m => m.MiGrupoComponent),
        data: {
          title: 'Empresas del grupo',
          subtitle: 'Empresas vinculadas a tu grupo empresarial',
          breadcrumbs: [
            { label: 'Vista del grupo', url: '/home/mi-grupo' },
            { label: 'Empresas' },
          ],
        },
      },
      {
        path: 'clientes/:id/users',
        loadComponent: () => import('./pages/dashboard/customers/customer-user-management/customer-user-management').then(m => m.CustomerUserManagement),
        data: { title: 'Gestión de usuarios', subtitle: 'Usuarios asociados al cliente seleccionado' },
      },
      {
        path: 'clientes/:clienteId/cumplimiento',
        canActivate: [adminGuard],
        loadComponent: () => import('./pages/dashboard/customer-compliance-status/customer-compliance-status').then(m => m.CustomerComplianceStatus),
        data: {
          title: 'Estado de cumplimiento',
          subtitle: 'Resumen global del cliente',
          breadcrumbs: [
            { label: 'Clientes', url: '/home/clientes' },
            { label: 'Cumplimiento normativo' },
          ],
        },
      },
      {
        path: 'clientes/:clienteId/implantaciones',
        loadComponent: () => import('./pages/dashboard/implantations/implantations-list/implantations-list').then(m => m.ImplantationsList),
        data: { title: 'Implantación de proyectos', subtitle: '' },
      },
      {
        path: 'clientes/:clienteId/implantaciones/:implantacionId',
        loadComponent: () => import('./pages/dashboard/implantations/implantation-detail/implantation-detail').then(m => m.ImplantationDetail),
        data: { title: 'Implantación', subtitle: '' },
      },
      ...(FEATURE_FLAGS.digitalDisconnection ? ([
        {
          path: 'clientes/:clienteId/desconexion-digital',
          canActivate: [adminGuard],
          loadComponent: () => import('./pages/dashboard/digital-disconnection/disconnection-company/disconnection-company').then(m => m.DisconnectionCompany),
          data: {
            title: 'Desconexión Digital',
            subtitle: 'Protocolos de desconexión digital de la empresa cliente',
          },
        },
      ] satisfies Routes) : ([
        {
          path: 'clientes/:clienteId/desconexion-digital',
          children: [
            { path: '', redirectTo: '/home/dashboard', pathMatch: 'full' },
            { path: '**', redirectTo: '/home/dashboard' },
          ],
        },
      ] satisfies Routes)),
      // 2026-08-03: modulo de empleados desactivado temporalmente; se conserva la ruta original para reactivacion futura.
      // {
      //   path: 'empresas/:clienteId/personal',
      //   loadComponent: () => import('./pages/dashboard/employees/employees').then(m => m.Employees),
      //   data: {
      //     title: 'Personal',
      //     subtitle: 'Gestiona el equipo de la empresa',
      //     breadcrumbs: [
      //       { label: 'Clientes', url: '/home/clientes' },
      //       { label: 'Personal' },
      //     ],
      //   },
      // },
      {
        path: 'empresas/:clienteId/personal',
        redirectTo: '/home/clientes',
        pathMatch: 'full',
      },
      {
        path: 'clientes',
        loadComponent: () => import('./pages/dashboard/customers/customers').then(m => m.Customers),
        data: { title: 'Clientes', subtitle: 'Gestión de todas las empresas' },
      },
      {
        path: 'clientes/:id',
        redirectTo: 'clientes',
        pathMatch: 'full',
      },
      {
        path: 'grupos',
        loadComponent: () => import('./pages/dashboard/enterprise-groups/enterprise-groups').then(m => m.EnterpriseGroups),
        data: { title: 'Grupos empresariales', subtitle: 'Agrupación de empresas cliente bajo una matriz' },
      },
      {
        path: 'grupos/:id',
        loadComponent: () => import('./pages/dashboard/enterprise-groups/enterprise-group-detail/enterprise-group-detail').then(m => m.EnterpriseGroupDetailComponent),
        data: { title: 'Detalle de grupo', subtitle: 'Vista de empresas y configuración del grupo' },
      },
      // 2026-08-04: modulo de presupuestos retirado de la interfaz; se conserva la ruta original comentada para reactivacion futura.
      // {
      //   path: 'presupuestos',
      //   loadComponent: () => import('./pages/dashboard/quotes/quotes').then(m => m.Quotes),
      //   data: { title: 'Presupuestos', subtitle: 'Presupuestos enviados a clientes' },
      // },
      {
        path: 'presupuestos',
        children: [
          { path: '', redirectTo: '/home/clientes', pathMatch: 'full' },
          { path: 'nuevo', redirectTo: '/home/clientes', pathMatch: 'full' },
          { path: ':id/editar', redirectTo: '/home/clientes', pathMatch: 'full' },
          { path: ':id', redirectTo: '/home/clientes', pathMatch: 'full' },
          { path: '**', redirectTo: '/home/clientes' },
        ],
      },
      {
        path: 'avisos',
        loadComponent: () => import('./pages/dashboard/alerts/alerts').then(m => m.Alerts),
        data: { title: 'Centro de avisos', subtitle: 'Alertas accionables sobre tu cartera de clientes' },
      },
      {
        path: 'noticias-normativas',
        loadComponent: () => import('./pages/dashboard/regulatory-news-listing/regulatory-news-listing').then(m => m.RegulatoryNewsListing),
        data: { title: 'Noticias normativas', subtitle: '' },
      },
      // 2026-08-04: modulo de solicitudes de activacion retirado del area cliente; acceso directo redirigido sin error.
      // Ruta de cliente anterior reservada para reactivacion futura: path 'solicitudes'.
      {
        path: 'solicitudes',
        redirectTo: '/home/inicio',
        pathMatch: 'full',
      },
      {
        path: 'solicitudes-activacion',
        canActivate: [adminGuard],
        loadComponent: () => import('./pages/dashboard/activation-requests/activation-requests').then(m => m.ActivationRequests),
        data: {
          title: 'Solicitudes de activación',
          subtitle: 'Gestiona las solicitudes de activación de módulos de los clientes',
        },
      },
      ...(FEATURE_FLAGS.digitalDisconnection ? ([
        {
          path: 'desconexion-digital',
          canActivate: [adminGuard],
          loadComponent: () => import('./pages/dashboard/digital-disconnection/disconnection-global/disconnection-global').then(m => m.DisconnectionGlobal),
          data: {
            title: 'Desconexión Digital',
            subtitle: 'Protocolos comunicados, aceptaciones y certificados de cumplimiento',
          },
        },
        {
          path: 'desconexion-digital/clientes/:clienteId',
          canActivate: [adminGuard],
          loadComponent: () => import('./pages/dashboard/digital-disconnection/disconnection-company/disconnection-company').then(m => m.DisconnectionCompany),
          data: {
            title: 'Desconexión Digital',
            subtitle: 'Protocolos de desconexión digital de la empresa cliente',
          },
        },
        {
          path: 'desconexion-digital/clientes/:clienteId/protocolos/:protocoloId/editar',
          canActivate: [adminGuard],
          loadComponent: () => import('./pages/dashboard/digital-disconnection/disconnection-editor/disconnection-editor').then(m => m.DisconnectionEditor),
          data: {
            title: 'Edición del protocolo',
            subtitle: '',
            breadcrumbs: [
              { label: 'Desconexión Digital', url: '/home/desconexion-digital' },
              { label: 'Edición' },
            ],
          },
        },
        {
          path: 'desconexion-digital/clientes/:clienteId/protocolos/:protocoloId',
          canActivate: [adminGuard],
          loadComponent: () => import('./pages/dashboard/digital-disconnection/disconnection-detail/disconnection-detail').then(m => m.DisconnectionDetail),
          data: {
            title: 'Detalle de protocolo',
            subtitle: '',
            breadcrumbs: [
              { label: 'Desconexión Digital', url: '/home/desconexion-digital' },
              { label: 'Detalle' },
            ],
          },
        },
        {
          path: 'me/desconexion-digital',
          loadComponent: () => import('./pages/dashboard/digital-disconnection/disconnection-client/disconnection-client').then(m => m.DisconnectionClient),
          data: {
            title: 'Desconexión Digital',
            subtitle: 'Protocolos de desconexión digital generados, firmados por empleados y certificados.',
            clientView: true,
            breadcrumbs: [
              { label: 'Inicio', url: '/home/inicio' },
              { label: 'Desconexión Digital' },
            ],
          },
        },
        {
          path: 'me/desconexion-digital/protocolos/:protocoloId',
          loadComponent: () => import('./pages/dashboard/digital-disconnection/disconnection-detail/disconnection-detail').then(m => m.DisconnectionDetail),
          data: {
            title: 'Detalle de protocolo',
            subtitle: '',
            clientView: true,
            breadcrumbs: [
              { label: 'Desconexión Digital', url: '/home/me/desconexion-digital' },
              { label: 'Detalle' },
            ],
          },
        },
        {
          path: 'desconexion',
          redirectTo: 'desconexion-digital',
          pathMatch: 'full',
        },
      ] satisfies Routes) : ([
        {
          path: 'desconexion-digital',
          children: [
            { path: '', redirectTo: '/home/dashboard', pathMatch: 'full' },
            { path: '**', redirectTo: '/home/dashboard' },
          ],
        },
        {
          path: 'me/desconexion-digital',
          children: [
            { path: '', redirectTo: '/home/inicio', pathMatch: 'full' },
            { path: '**', redirectTo: '/home/inicio' },
          ],
        },
        {
          path: 'desconexion',
          redirectTo: '/home/dashboard',
          pathMatch: 'full',
        },
      ] satisfies Routes)),
      ...(FEATURE_FLAGS.complianceLaboral ? [] : ([
        {
          path: 'cumplimiento/laboral',
          redirectTo: '/home/dashboard',
          pathMatch: 'full',
        },
        {
          path: 'cumplimiento/compliance-laboral',
          redirectTo: '/home/dashboard',
          pathMatch: 'full',
        },
        {
          path: 'cumplimiento/desconexion-digital',
          redirectTo: '/home/dashboard',
          pathMatch: 'full',
        },
        {
          path: 'cumplimiento/desconexion_digital',
          redirectTo: '/home/dashboard',
          pathMatch: 'full',
        },
      ] satisfies Routes)),
      {
        path: 'compliance-laboral/igualdad',
        loadComponent: () => import('./pages/dashboard/compliance-laboral/compliance-laboral-info/compliance-laboral-info').then(m => m.ComplianceLaboralInfo),
        data: {
          title: 'Plan de igualdad',
          subtitle: 'Plan de Igualdad y su ciclo de vida',
          slug: 'igualdad',
          breadcrumbs: [
            { label: 'Compliance Laboral' },
            { label: 'Plan de igualdad' },
          ],
        },
      },
      {
        path: 'compliance-laboral/registro-retributivo',
        loadComponent: () => import('./pages/dashboard/compliance-laboral/compliance-laboral-info/compliance-laboral-info').then(m => m.ComplianceLaboralInfo),
        data: {
          title: 'Registro retributivo',
          subtitle: 'Registro retributivo y transparencia salarial',
          slug: 'registro-retributivo',
          breadcrumbs: [
            { label: 'Compliance Laboral' },
            { label: 'Registro retributivo' },
          ],
        },
      },
      {
        path: 'compliance-laboral/acoso-laboral-sexual',
        loadComponent: () => import('./pages/dashboard/compliance-laboral/compliance-laboral-info/compliance-laboral-info').then(m => m.ComplianceLaboralInfo),
        data: {
          title: 'Acoso laboral y sexual',
          subtitle: 'Protocolo de prevención y actuación',
          slug: 'acoso-laboral-sexual',
          breadcrumbs: [
            { label: 'Compliance Laboral' },
            { label: 'Acoso laboral y sexual' },
          ],
        },
      },
      {
        path: 'compliance-laboral/control-horario',
        loadComponent: () => import('./pages/dashboard/compliance-laboral/compliance-laboral-info/compliance-laboral-info').then(m => m.ComplianceLaboralInfo),
        data: {
          title: 'Control horario',
          subtitle: 'Registro de la jornada laboral',
          slug: 'control-horario',
          breadcrumbs: [
            { label: 'Compliance Laboral' },
            { label: 'Control horario' },
          ],
        },
      },
      {
        path: 'compliance-laboral/canal-denuncias',
        loadComponent: () => import('./pages/dashboard/compliance-laboral/compliance-laboral-info/compliance-laboral-info').then(m => m.ComplianceLaboralInfo),
        data: {
          title: 'Canal de denuncias',
          subtitle: 'Canal interno de información',
          slug: 'canal-denuncias',
          breadcrumbs: [
            { label: 'Compliance Laboral' },
            { label: 'Canal de denuncias' },
          ],
        },
      },
      {
        path: 'nis2',
        loadComponent: () => import('./pages/dashboard/compliance-laboral/compliance-laboral-info/compliance-laboral-info').then(m => m.ComplianceLaboralInfo),
        data: {
          title: 'Directiva NIS2',
          subtitle: 'Ciberseguridad y gestión de riesgos NIS2',
          slug: 'nis2',
        },
      },
      {
        path: 'cumplimiento/formularios',
        redirectTo: 'formularios/mis-formularios',
        pathMatch: 'full',
      },
      // 2026-08-04: resumen de proyectos ad-hoc oculto de la interfaz; el acceso directo redirige sin mostrar error.
      {
        path: 'cumplimiento/proyectos',
        redirectTo: 'clientes',
        pathMatch: 'full',
      },
      {
        path: 'cumplimiento/:modulo',
        canActivate: [adminGuard],
        loadComponent: () => import('./pages/dashboard/cumplimiento-summary/cumplimiento-summary').then(m => m.CumplimientoSummary),
        data: {
          title: 'Cumplimiento',
          subtitle: 'Resumen cross-cliente por módulo',
        },
      },
      {
        path: 'normativa',
        loadComponent: () => import('./pages/dashboard/regulations/regulations').then(m => m.Regulations),
        data: { title: 'Normativa', subtitle: 'Biblioteca de normativa oficial. Mantén actualizada la documentación vigente que afecta a tus clientes' },
      },
      {
        path: 'consultas/:id',
        loadComponent: () => import('./pages/dashboard/queries/query-detail/query-detail').then(m => m.QueryDetail),
        data: { title: 'Consultas', subtitle: '' },
      },
      {
        path: 'documentos',
        loadComponent: () => import('./pages/dashboard/document-manager/document-manager').then(m => m.DocumentManager),
        data: { title: 'Documentos', subtitle: 'Documentación y carpetas de compliance de tu empresa' },
      },
      {
        path: 'documentos/plantillas/nueva',
        canActivate: [adminGuard],
        loadComponent: () => import('./pages/dashboard/maintenance-templates/template-editor/template-editor').then(m => m.TemplateEditor),
        data: {
          title: 'Editor de plantilla',
          subtitle: 'Crea el contenido y las variables de la plantilla documental',
          breadcrumbs: [
            { label: 'Documentos', url: '/home/documentos' },
            { label: 'Plantillas de documentos', url: '/home/documentos/plantillas' },
            { label: 'Nueva plantilla' },
          ],
        },
      },
      {
        path: 'documentos/plantillas/:id/editar',
        canActivate: [adminGuard],
        loadComponent: () => import('./pages/dashboard/maintenance-templates/template-editor/template-editor').then(m => m.TemplateEditor),
        data: {
          title: 'Editor de plantilla',
          subtitle: 'Edita el contenido y las variables de la plantilla documental',
          breadcrumbs: [
            { label: 'Documentos', url: '/home/documentos' },
            { label: 'Plantillas de documentos', url: '/home/documentos/plantillas' },
            { label: 'Editar plantilla' },
          ],
        },
      },
      {
        path: 'documentos/plantillas',
        canActivate: [adminGuard],
        loadComponent: () => import('./pages/dashboard/maintenance-templates/maintenance-templates').then(m => m.MaintenanceTemplates),
        data: {
          title: 'Plantillas de documentos',
          subtitle: '',
          breadcrumbs: [
            { label: 'Documentos', url: '/home/documentos' },
            { label: 'Plantillas de documentos' },
          ],
        },
      },
      {
        path: 'documentos/carpetas/:id',
        loadComponent: () => import('./pages/dashboard/document-manager/folder-content/folder-content').then(m => m.FolderContent),
        data: {
          title: 'Documentos',
          subtitle: '',
          breadcrumbs: [
            { label: 'Documentos', url: '/home/documentos' },
            { label: 'Ver documentos' },
          ],
        },
      },
      {
        path: 'mantenimiento',
        redirectTo: 'documentos',
        pathMatch: 'full',
      },
      {
        path: 'mantenimiento/plantillas',
        redirectTo: 'documentos/plantillas',
        pathMatch: 'full',
      },
      {
        path: 'mantenimiento/plantillas/nueva',
        redirectTo: 'documentos/plantillas/nueva',
        pathMatch: 'full',
      },
      {
        path: 'mantenimiento/plantillas/:id/editar',
        redirectTo: 'documentos/plantillas/:id/editar',
        pathMatch: 'full',
      },
      {
        path: 'mantenimiento/carpetas/:id',
        redirectTo: 'documentos/carpetas/:id',
        pathMatch: 'full',
      },
      {
        path: 'mis-formularios',
        loadComponent: () => import('./pages/dashboard/forms/client-forms-list/client-forms-list').then(m => m.ClientFormsList),
        data: { title: 'Mis formularios', subtitle: 'Formularios que tu asesoría te ha enviado. Responde para mantener tu cumplimiento al día.' },
      },
      {
        path: 'mis-formularios/:id/responder',
        loadComponent: () => import('./pages/dashboard/forms/client-form-responder/client-form-responder').then(m => m.ClientFormResponder),
        data: {
          title: 'Cumplimentar formulario',
          subtitle: '',
          breadcrumbs: [
            { label: 'Mis formularios', url: '/home/mis-formularios' },
            { label: 'Cumplimentar formulario' },
          ],
        },
      },
      {
        path: 'mis-formularios/:id/ver',
        loadComponent: () => import('./pages/dashboard/forms/client-form-responder/client-form-responder').then(m => m.ClientFormResponder),
        data: {
          title: 'Ver formulario',
          subtitle: '',
          breadcrumbs: [
            { label: 'Mis formularios', url: '/home/mis-formularios' },
            { label: 'Ver formulario' },
          ],
        },
      },
      {
        path: 'me/rat',
        loadComponent: () => import('./pages/dashboard/rat/rat').then(m => m.Rat),
        data: { title: 'Registro de Actividades', subtitle: 'Consulta el RAT de tu empresa', clientView: true },
      },
      {
        path: 'me/faqs',
        loadComponent: () => import('./pages/dashboard/faqs-client/faqs-client').then(m => m.FaqsClient),
        data: { title: 'Centro de ayuda', subtitle: 'Respuestas a las preguntas más frecuentes sobre tu cumplimiento, mantenidas por tu asesoría' },
      },
      // 2026-08-03: modulo de empleados desactivado temporalmente; se conserva la ruta de cliente para reactivacion futura.
      // {
      //   path: 'personal',
      //   loadComponent: () => import('./pages/dashboard/employees/employees').then(m => m.Employees),
      //   data: { title: 'Personal', subtitle: 'Gestiona el equipo de la empresa' },
      // },
      {
        path: 'personal',
        redirectTo: '/home/inicio',
        pathMatch: 'full',
      },
      // 2026-08-03: modulo de empleados desactivado temporalmente; se conserva el alias legado para reactivacion futura.
      // {
      //   path: 'me/empleados',
      //   redirectTo: '/home/personal',
      //   pathMatch: 'full',
      // },
      {
        path: 'me/empleados',
        redirectTo: '/home/inicio',
        pathMatch: 'full',
      },
      {
        path: 'me/faqs/:id',
        loadComponent: () => import('./pages/dashboard/faqs-client/faqs-client').then(m => m.FaqsClient),
        data: {
          title: 'Centro de ayuda',
          subtitle: 'Respuestas a las preguntas más frecuentes sobre tu cumplimiento, mantenidas por tu asesoría',
          breadcrumbs: [
            { label: 'Centro de ayuda', url: '/home/me/faqs' },
            { label: 'Ver respuesta' },
          ],
        },
      },
      // 86cb2d1fm: la vista de fases/tareas/progreso del cliente se sustituye por la vista
      // simplificada de RGPD (carpetas Implantación/Mantenimiento + certificado). Alias
      // conservados por si hay enlaces/favoritos antiguos apuntando a las rutas previas.
      {
        path: 'me/implantaciones',
        redirectTo: 'me/rgpd',
        pathMatch: 'full',
      },
      {
        path: 'me/implantaciones/:implantacionId',
        redirectTo: 'me/rgpd',
        pathMatch: 'full',
      },
      {
        path: 'me/rgpd',
        loadComponent: () => import('./pages/dashboard/client-rgpd-home/client-rgpd-home').then(m => m.ClientRgpdHome),
        data: { title: 'Reglamento General de Protección de Datos', subtitle: 'RGPD' },
      },
      {
        path: 'me/rgpd/implantacion',
        loadComponent: () => import('./pages/dashboard/client-rgpd-folder/client-rgpd-folder').then(m => m.ClientRgpdFolder),
        data: { title: 'Implantación', subtitle: '', folder: 'implantacion' },
      },
      {
        path: 'me/rgpd/mantenimiento',
        loadComponent: () => import('./pages/dashboard/client-rgpd-mantenimiento/client-rgpd-mantenimiento').then(m => m.ClientRgpdMantenimiento),
        data: { title: 'Mantenimiento', subtitle: '' },
      },
      // 86cb2dr49: vista simplificada de IA Act para el cliente (resumen de solo lectura),
      // sustituye la pantalla de firma de política que veía antes en '/home/ia-act/politica'.
      // Esa ruta sigue existiendo tal cual para la gestoría (gestión de política/firmas).
      {
        path: 'me/ai-act',
        loadComponent: () => import('./pages/dashboard/client-ai-act/client-ai-act').then(m => m.ClientAiAct),
        data: { title: 'Ley de Inteligencia Artificial', subtitle: 'IA Act - Normativa europea sobre inteligencia artificial' },
      },
      // 2026-08-04: modulo de proyectos ad-hoc oculto de la interfaz; se conservan las rutas de cliente originales comentadas para reactivacion futura.
      // {
      //   path: 'me/proyectos',
      //   loadComponent: () => import('./pages/dashboard/implantations/implantations-list/implantations-list').then(m => m.ImplantationsList),
      //   data: { title: 'Proyectos', subtitle: '', clientView: true },
      // },
      // {
      //   path: 'me/proyectos/:implantacionId',
      //   loadComponent: () => import('./pages/dashboard/implantations/implantation-detail/implantation-detail').then(m => m.ImplantationDetail),
      //   data: { title: 'Proyecto', subtitle: '', clientView: true },
      // },
      {
        path: 'me/proyectos',
        children: [
          { path: '', redirectTo: '/home/inicio', pathMatch: 'full' },
          { path: ':implantacionId', redirectTo: '/home/inicio', pathMatch: 'full' },
          { path: '**', redirectTo: '/home/inicio' },
        ],
      },
      // 2026-08-04: modulo de proyectos ad-hoc oculto de la interfaz; se conserva el listado original comentado para reactivacion futura.
      // {
      //   path: 'proyectos',
      //   loadComponent: () => import('./pages/dashboard/projects/projects').then(m => m.Projects),
      //   data: { title: 'Mis proyectos', subtitle: 'Proyectos personalizados disponibles para asignar a tus clientes' },
      // },
      // 2026-08-04: modulo de proyectos ad-hoc oculto de la interfaz; se conserva la ruta de sectores original comentada para reactivacion futura.
      // {
      //   path: 'proyectos/sectores',
      //   loadComponent: () => import('./pages/dashboard/projects/sectors/sectors').then(m => m.Sectors),
      //   data: {
      //     title: 'Sectores',
      //     subtitle: '',
      //     breadcrumbs: [
      //       { label: 'Mis proyectos', url: '/home/proyectos' },
      //       { label: 'Sectores' },
      //     ],
      //   },
      // },
      // 2026-08-04: modulo de proyectos ad-hoc oculto de la interfaz; se conserva la ruta de detalle/asignacion original comentada para reactivacion futura.
      // {
      //   path: 'proyectos/:id/clientes',
      //   loadComponent: () => import('./pages/dashboard/projects/project-clients/project-clients').then(m => m.ProjectClients),
      //   data: {
      //     title: 'Detalle de proyecto',
      //     subtitle: 'Estado de cumplimiento de los clientes asignados',
      //     breadcrumbs: [
      //       { label: 'Mis proyectos', url: '/home/proyectos' },
      //       { label: 'Detalle' },
      //     ],
      //   },
      // },
      // 2026-08-04: modulo de proyectos ad-hoc oculto de la interfaz; se conserva la ruta de edicion original comentada para reactivacion futura.
      // {
      //   path: 'proyectos/:id',
      //   loadComponent: () => import('./pages/dashboard/projects/project-editor/project-editor').then(m => m.ProjectEditor),
      //   data: {
      //     title: 'Editor de proyecto',
      //     subtitle: '',
      //     breadcrumbs: [
      //       { label: 'Mis proyectos', url: '/home/proyectos' },
      //       { label: 'Editar' },
      //     ],
      //   },
      // },
      {
        path: 'proyectos',
        children: [
          { path: '', redirectTo: '/home/clientes', pathMatch: 'full' },
          { path: 'nuevo', redirectTo: '/home/clientes', pathMatch: 'full' },
          { path: 'sectores', redirectTo: '/home/clientes', pathMatch: 'full' },
          { path: ':id/clientes', redirectTo: '/home/clientes', pathMatch: 'full' },
          { path: ':id/editar', redirectTo: '/home/clientes', pathMatch: 'full' },
          { path: ':id', redirectTo: '/home/clientes', pathMatch: 'full' },
          { path: '**', redirectTo: '/home/clientes' },
        ],
      },
      {
        path: 'faqs',
        loadComponent: () => import('./pages/dashboard/faqs/faqs').then(m => m.Faqs),
        data: { title: 'Centro de ayuda', subtitle: 'Gestiona las preguntas frecuentes y las consultas de tus clientes' },
      },
      {
        path: 'faqs/estadisticas',
        loadComponent: () => import('./pages/dashboard/faqs/faqs-stats/faqs-stats').then(m => m.FaqsStats),
        data: {
          title: 'Estadísticas del centro de ayuda',
          subtitle: 'Métricas de uso, valoraciones y huecos de contenido',
          breadcrumbs: [
            { label: 'Centro de ayuda', url: '/home/faqs' },
            { label: 'Estadísticas' },
          ],
        },
      },
      {
        path: 'rat',
        redirectTo: 'cumplimiento/rgpd',
        pathMatch: 'full',
      },
      {
        path: 'ia-act',
        loadComponent: () => import('./pages/dashboard/ai-act/ai-act').then(m => m.AiAct),
        data: {
          title: 'Cumplimiento IA Act',
          subtitle: 'Gestión del cumplimiento del Reglamento de Inteligencia Artificial de la UE',
        },
      },
      {
        path: 'cumplimiento-ia',
        redirectTo: 'ia-act',
        pathMatch: 'full',
      },
      {
        path: 'ia-act/inventario',
        loadComponent: () => import('./pages/dashboard/ai-act-inventory/ai-act-inventory').then(m => m.AiActInventory),
        data: {
          title: 'Inventario de sistemas IA',
          subtitle: 'Sistemas, clasificación y evidencias del IA Act',
          breadcrumbs: [
            { label: 'Cumplimiento IA Act', url: '/home/ia-act' },
            { label: 'Inventario' },
          ],
        },
      },
      {
        path: 'ia-act/inventario/:sistemaId/clasificar',
        loadComponent: () => import('./pages/dashboard/ai-act-classifier/ai-act-classifier').then(m => m.AiActClassifier),
        data: {
          title: 'Clasificador IA Act',
          subtitle: 'Clasificación interactiva del sistema de IA',
          breadcrumbs: [
            { label: 'Cumplimiento IA Act', url: '/home/ia-act' },
            { label: 'Inventario', url: '/home/ia-act/inventario' },
            { label: 'Clasificar' },
          ],
        },
      },
      {
        path: 'ia-act/inventario/:sistemaId/sinergia-rgpd',
        loadComponent: () => import('./pages/dashboard/ai-act-synergy/ai-act-synergy').then(m => m.AiActSynergy),
        data: {
          title: 'Sinergia IA Act - RGPD',
          subtitle: 'Checklist cruzado del sistema de IA',
          breadcrumbs: [
            { label: 'Cumplimiento IA Act', url: '/home/ia-act' },
            { label: 'Inventario', url: '/home/ia-act/inventario' },
            { label: 'Sinergia RGPD' },
          ],
        },
      },
      {
        path: 'ia-act/formaciones',
        loadComponent: () => import('./pages/dashboard/ai-act-trainings/ai-act-trainings').then(m => m.AiActTrainings),
        data: {
          title: 'Plan de alfabetización en IA',
          subtitle: 'Art. 4 RIA · Formación y evidencias del personal',
          breadcrumbs: [
            { label: 'Cumplimiento IA Act', url: '/home/ia-act' },
            { label: 'Formaciones' },
          ],
        },
      },
      {
        path: 'ia-act/formacion',
        redirectTo: 'ia-act/formaciones',
        pathMatch: 'full',
      },
      {
        path: 'ia-act/politica',
        loadComponent: () => import('./pages/dashboard/ai-act-policy/ai-act-policy').then(m => m.AiActPolicy),
        data: {
          title: 'Política interna de IA',
          subtitle: 'Publicación, aceptación y firma de la política interna de IA',
          breadcrumbs: [
            { label: 'Cumplimiento IA Act', url: '/home/ia-act' },
            { label: 'Política interna' },
          ],
        },
      },
      {
        path: 'ia-act/sinergia-rgpd',
        loadComponent: () => import('./pages/dashboard/ai-act-synergy/ai-act-synergy').then(m => m.AiActSynergy),
        data: {
          title: 'Sinergia RGPD · IA Act',
          subtitle: 'Checklist de conexiones entre tratamientos RGPD y sistemas de IA',
          breadcrumbs: [
            { label: 'Cumplimiento IA Act', url: '/home/ia-act' },
            { label: 'Sinergia RGPD' },
          ],
        },
      },
      {
        path: 'clientes/:clienteId/rat',
        loadComponent: () => import('./pages/dashboard/rat/rat').then(m => m.Rat),
        data: { title: 'RAT', subtitle: 'Registro de Actividades de Tratamiento' },
      },
      {
        path: 'formularios',
        children: [
          {
            path: '',
            redirectTo: 'mis-formularios',
            pathMatch: 'full',
          },
          {
            path: 'mis-formularios',
            loadComponent: () => import('./pages/dashboard/forms/forms-list/forms-list').then(m => m.FormsList),
            data: { title: 'Formularios', subtitle: 'Crea y comparte cuestionarios con tus clientes' },
          },
          {
            path: 'banco',
            loadComponent: () => import('./pages/dashboard/forms/question-bank/question-bank').then(m => m.QuestionBank),
            data: {
              title: 'Banco de preguntas',
              subtitle: '',
              breadcrumbs: [
                { label: 'Formularios', url: '/home/formularios/mis-formularios' },
                { label: 'Banco de preguntas' },
              ],
            },
          },
          {
            path: ':id/editar',
            loadComponent: () => import('./pages/dashboard/forms/form-editor/form-editor').then(m => m.FormEditor),
            data: {
              title: 'Editor de formulario',
              subtitle: '',
              breadcrumbs: [
                { label: 'Formularios', url: '/home/formularios/mis-formularios' },
                { label: 'Editar' },
              ],
            },
          },
          {
            path: ':id/compartir',
            loadComponent: () => import('./pages/dashboard/forms/form-share/form-share').then(m => m.FormShare),
            data: {
              title: 'Compartir formulario',
              subtitle: '',
              breadcrumbs: [
                { label: 'Formularios', url: '/home/formularios/mis-formularios' },
                { label: 'Compartir' },
              ],
            },
          },
          {
            path: ':id/asignaciones',
            loadComponent: () => import('./pages/dashboard/forms/form-assignments/form-assignments').then(m => m.FormAssignments),
            data: {
              title: 'Asignación del formulario',
              subtitle: '',
              breadcrumbs: [
                { label: 'Formularios', url: '/home/formularios/mis-formularios' },
                { label: 'Ver asignaciones' },
              ],
            },
          },
          {
            path: ':id/asignaciones/:asignacionId/respuestas',
            loadComponent: () => import('./pages/dashboard/forms/form-responses/form-responses').then(m => m.FormResponses),
            data: {
              title: 'Respuestas del formulario',
              subtitle: '',
              breadcrumbs: [
                { label: 'Formularios', url: '/home/formularios/mis-formularios' },
                { label: 'Ver respuestas' },
              ],
            },
          },
        ],
      },
    ]
  },
  {
    path: 'avisos',
    redirectTo: 'home/avisos',
    pathMatch: 'full',
  },
  {
    path: 'mantenimiento',
    redirectTo: 'home/documentos',
    pathMatch: 'full',
  },
  {
    path: 'documentos',
    redirectTo: 'home/documentos',
    pathMatch: 'full',
  },
  {
    path: 'mantenimiento/plantillas',
    redirectTo: 'home/documentos/plantillas',
    pathMatch: 'full',
  },
  {
    path: 'documentos/plantillas',
    redirectTo: 'home/documentos/plantillas',
    pathMatch: 'full',
  },
  {
    path: 'mantenimiento/plantillas/nueva',
    redirectTo: 'home/documentos/plantillas/nueva',
    pathMatch: 'full',
  },
  {
    path: 'documentos/plantillas/nueva',
    redirectTo: 'home/documentos/plantillas/nueva',
    pathMatch: 'full',
  },
  {
    path: 'mantenimiento/plantillas/:id/editar',
    redirectTo: 'home/documentos/plantillas/:id/editar',
    pathMatch: 'full',
  },
  {
    path: 'mantenimiento/carpetas/:id',
    redirectTo: 'home/documentos/carpetas/:id',
    pathMatch: 'full',
  },
  {
    path: 'documentos/carpetas/:id',
    redirectTo: 'home/documentos/carpetas/:id',
    pathMatch: 'full',
  },
  {
    path: 'documentos/plantillas/:id/editar',
    redirectTo: 'home/documentos/plantillas/:id/editar',
    pathMatch: 'full',
  },
  // 2026-08-03: modulo de empleados desactivado temporalmente; se conserva el redirect raiz para reactivacion futura.
  // {
  //   path: 'personal',
  //   redirectTo: 'home/personal',
  //   pathMatch: 'full',
  // },
  {
    path: 'personal',
    redirectTo: 'home/inicio',
    pathMatch: 'full',
  },
  // 2026-08-03: modulo de empleados desactivado temporalmente; se conserva el redirect raiz de empresa para reactivacion futura.
  // {
  //   path: 'empresas/:clienteId/personal',
  //   redirectTo: 'home/empresas/:clienteId/personal',
  //   pathMatch: 'full',
  // },
  {
    path: 'empresas/:clienteId/personal',
    redirectTo: 'home/clientes',
    pathMatch: 'full',
  },
  {
    path: 'me/rat',
    redirectTo: 'home/me/rat',
    pathMatch: 'full',
  },
  {
    path: 'ia-act',
    redirectTo: 'home/ia-act',
    pathMatch: 'full',
  },
  {
    path: 'cumplimiento/formularios',
    redirectTo: 'home/formularios/mis-formularios',
    pathMatch: 'full',
  },
  {
    path: 'cumplimiento/:modulo',
    redirectTo: 'home/cumplimiento/:modulo',
    pathMatch: 'full',
  },
  {
    path: 'ia-act/formaciones',
    redirectTo: 'home/ia-act/formaciones',
    pathMatch: 'full',
  },
  {
    path: 'ia-act/formacion',
    redirectTo: 'home/ia-act/formaciones',
    pathMatch: 'full',
  },
  {
    path: 'ia-act/politica',
    redirectTo: 'home/ia-act/politica',
    pathMatch: 'full',
  },
  {
    path: 'ia-act/sinergia-rgpd',
    redirectTo: 'home/ia-act/sinergia-rgpd',
    pathMatch: 'full',
  },
  {
    path: 'ia-act/inventario/:sistemaId/sinergia-rgpd',
    redirectTo: 'home/ia-act/inventario/:sistemaId/sinergia-rgpd',
    pathMatch: 'full',
  },
  {
    path: 'cumplimiento-ia',
    redirectTo: 'home/ia-act',
    pathMatch: 'full',
  },
  {
    path: 'grupos',
    redirectTo: 'home/grupos',
    pathMatch: 'full',
  },
  {
    path: '',
    redirectTo: 'home',
    pathMatch: 'full'
  },
  {
    path: '**',
    redirectTo: 'home'
  }
];
